import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

const faqs = [
  {
    q: '樱花漫画的漫画资源是否免费？',
    a: '是的！樱花漫画所有漫画资源均提供免费全集下拉式阅读。我们致力于为广大漫画爱好者提供优质的免费阅读体验，无需付费订阅即可畅享海量韩漫、日漫、国漫资源。',
  },
  {
    q: '如何在手机上阅读樱花漫画？',
    a: '樱花漫画网已针对移动端进行全面优化。您可以通过手机浏览器直接访问我们的网站，即可享受流畅的下拉式阅读体验。页面会自动适配您的手机屏幕尺寸，无需下载任何App。',
  },
  {
    q: '漫画多久更新一次？',
    a: '我们每天都会更新热门漫画的最新章节。连载中的漫画根据原作者的更新频率同步更新，一般为每周更新一至两话。您可以使用追更功能，在喜欢的漫画更新时第一时间收到通知。',
  },
  {
    q: '如何找到我想看的漫画？',
    a: '您可以通过多种方式找到喜欢的漫画：1) 在首页的分类导航中按韩漫、日漫、国漫浏览；2) 使用搜索功能输入漫画名称；3) 在分类页面中按最新更新、最多阅读或最高评分排序；4) 查看首页的"猜你喜欢"个性化推荐。',
  },
  {
    q: '樱花漫画支持哪些类型的漫画？',
    a: '我们涵盖韩漫、日漫、国漫三大品类，涉及恋爱、热血、奇幻、武侠、校园、搞笑、穿越、悬疑、科幻、恐怖、古风、职场等多种题材标签。无论您喜欢什么类型的漫画，都能在樱花漫画找到心仪的作品。',
  },
  {
    q: '如何联系樱花漫画的客服？',
    a: '您可以通过以下方式联系我们：发送邮件至 contact@yinghuamanhua.com，或使用本页面的联系表单提交您的问题。我们的客服团队会在24小时内回复您的咨询。',
  },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      setFormMessage('提交成功！我们会尽快回复您。');
      form.reset();
      setTimeout(() => { setFormState('idle'); setFormMessage(''); }, 3000);
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9on1h2qs38sj6jc7bqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (res.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('提交成功！我们会尽快回复您。');
        form.reset();
      } else {
        setFormState('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试或直接发送邮件联系我们。');
      }
    } catch {
      setFormState('error');
      setFormMessage('网络异常，请稍后重试。');
    }
  };

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: '关于我们', path: '/about' },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500">
              {breadcrumbs.map((crumb, idx) => (
                <span key={crumb.path} className="flex items-center gap-2">
                  {idx > 0 && (
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  )}
                  {idx === breadcrumbs.length - 1 ? (
                    <span className="text-foreground-700 font-medium">{crumb.label}</span>
                  ) : (
                    <Link to={crumb.path} className="hover:text-primary-500 transition-colors cursor-pointer">
                      {crumb.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20romantic%20cherry%20blossom%20branches%20against%20warm%20cream%20sky%2C%20scattered%20pink%20petals%2C%20artistic%20Japanese%20illustration%20style%2C%20dreamy%20atmosphere%2C%20clean%20minimal%20composition%2C%20elegant%20and%20serene%2C%20soft%20gradient%20light&width=1920&height=500&seq=about-hero-sakura-03&orientation=landscape"
            alt="关于樱花漫画"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          <div className="relative z-10 max-w-[1280px] mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              关于樱花漫画
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              专业的在线漫画阅读平台，致力于为漫画爱好者提供优质的阅读体验
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    樱花漫画成立于2020年，是国内领先的在线漫画阅读平台。我们专注于为漫画爱好者提供海量优质漫画资源，涵盖<strong>韩漫、日漫、国漫</strong>三大品类，支持免费全集下拉式阅读。
                  </p>
                  <p>
                    自创立以来，樱花漫画始终秉持"让每个人都能享受漫画的乐趣"的理念，持续引进和更新热门漫画作品。我们与多家国内外漫画出版社和创作团队建立了合作关系，确保平台上的漫画资源丰富多样、更新及时。
                  </p>
                  <p>
                    目前，樱花漫画已积累超过<strong>100万活跃用户</strong>，累计阅读量突破5000万次。我们的用户遍布全国，涵盖了各个年龄段的漫画爱好者。无论是喜欢热血战斗的男生，还是钟爱甜蜜恋爱的女生，都能在樱花漫画找到属于自己的精彩故事。
                  </p>
                  <p>
                    未来，樱花漫画将继续扩大漫画资源库，优化阅读体验，并计划推出更多个性化推荐功能和社区互动模块，打造更加完善的漫画阅读生态。
                  </p>
                </div>
              </div>
              <div className="h-72 md:h-96 rounded-2xl overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20reading%20nook%20with%20warm%20lighting%2C%20open%20manga%20book%20on%20wooden%20table%2C%20cherry%20blossom%20branch%20in%20vase%2C%20soft%20throw%20blanket%2C%20warm%20cream%20and%20pink%20tones%2C%20comfortable%20home%20library%20atmosphere%2C%20clean%20cozy%20aesthetic%2C%20artistic%20photography&width=800&height=600&seq=about-brand-reading-04&orientation=landscape"
                  alt="樱花漫画 - 品牌文化"
                  title="樱花漫画 - 舒适的阅读体验"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '100万+', label: '活跃用户', icon: 'ri-user-heart-line' },
                { value: '5000万+', label: '累计阅读', icon: 'ri-eye-line' },
                { value: '24+', label: '精选漫画', icon: 'ri-book-open-line' },
                { value: '99.7%', label: '用户好评率', icon: 'ri-emotion-happy-line' },
              ].map((stat) => (
                <div key={stat.label} className="p-6 rounded-xl bg-background-50">
                  <div className="w-11 h-11 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                    <i className={`${stat.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
              我们的优势
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              为用户提供优质的漫画阅读服务
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-stack-line',
                  title: '海量资源库',
                  desc: '涵盖韩漫、日漫、国漫三大品类，持续更新热门漫画，满足不同读者的阅读偏好和口味需求。',
                  color: 'text-primary-500',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '下拉式阅读',
                  desc: '专为移动端优化的下拉式阅读界面，无需翻页，流畅丝滑的阅读体验让您沉浸在漫画的世界中。',
                  color: 'text-accent-500',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '免费全集',
                  desc: '所有漫画免费提供全集阅读，无需任何付费订阅。我们相信好的漫画应该被更多人看到。',
                  color: 'text-secondary-500',
                },
                {
                  icon: 'ri-notification-3-line',
                  title: '追更提醒',
                  desc: '关注您喜爱的漫画作品，开启追更功能。每当有新章节更新时，第一时间收到通知，不错过任何精彩。',
                  color: 'text-primary-600',
                },
                {
                  icon: 'ri-heart-line',
                  title: '个性推荐',
                  desc: '基于您的阅读历史和偏好，智能推荐您可能喜欢的漫画作品，发现更多符合口味的好漫画。',
                  color: 'text-accent-600',
                },
                {
                  icon: 'ri-device-line',
                  title: '多端适配',
                  desc: '完美适配手机、平板和电脑等多种设备。无论您在哪里、用什么设备，都能获得最佳的阅读体验。',
                  color: 'text-secondary-600',
                },
              ].map((feature) => (
                <div key={feature.title} className="p-6 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 mb-4">
                    <i className={`${feature.icon} text-xl ${feature.color}`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              关于樱花漫画的常见问题解答
            </p>

            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="rounded-xl bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                  >
                    <span className="font-heading text-sm font-semibold text-foreground-900 pr-4">
                      {faq.q}
                    </span>
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </div>
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何问题或建议？请随时与我们联系
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@yinghuamanhua.com', color: 'text-primary-500' },
                { icon: 'ri-wechat-line', title: '微信公众号', value: '樱花漫画', color: 'text-accent-500' },
                { icon: 'ri-weibo-line', title: '官方微博', value: '@樱花漫画官方', color: 'text-secondary-500' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-11 h-11 flex items-center justify-center mx-auto mb-3 rounded-full bg-background-200">
                    <i className={`${item.icon} text-xl ${item.color}`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-foreground-500">{item.value}</p>
                </div>
              ))}
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-2xl p-6 md:p-8"
            >
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>

              <div className="mb-2">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  消息内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                  placeholder="请输入您想咨询的内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-guard"
              />

              {formState !== 'idle' && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  formState === 'success'
                    ? 'bg-secondary-50 text-secondary-700'
                    : 'bg-primary-50 text-primary-700'
                }`}>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-200 text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line mr-2"></i>
                发送消息
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于樱花漫画',
            description: '樱花漫画成立于2020年，是国内领先的在线漫画阅读平台，提供韩漫、日漫、国漫免费全集下拉式阅读服务。',
            url: 'https://www.yinghuamanhua.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '樱花漫画',
              url: 'https://www.yinghuamanhua.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yinghuamanhua.com' },
                { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.yinghuamanhua.com/about' },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}